const dbVersion = 1;

export const removeDatabase = async (dbName: string) => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(dbName);
        request.onerror = () => {
            reject(request.error);
        };

        request.onsuccess = resolve;

        request.onblocked = function () {
            reject('blocked');
        };
    });
};

export class IndexedDatabase {
    private readonly dbName: string;
    private readonly objectStoreNames: string[];
    private readonly initPromise: Promise<IDBDatabase>;
    private db?: IDBDatabase;

    constructor(dbName: string, objectStoreNames: string[], version: number = dbVersion) {
        this.dbName = dbName;
        this.objectStoreNames = objectStoreNames;

        this.initPromise = new Promise((resolve, reject) => {
            const openDBRequest = indexedDB.open(dbName, version);
            openDBRequest.onerror = () => reject(openDBRequest.error);

            openDBRequest.onsuccess = () => {
                this.db = openDBRequest.result;
                resolve(this.db);
            };

            openDBRequest.onupgradeneeded = () => {
                for (const objectStoreName of objectStoreNames) {
                    openDBRequest.result.createObjectStore(objectStoreName);
                }
            };
        });
    }

    public async put<T>(objectStoreName: string, key: IDBValidKey, data: T): Promise<void> {
        return this.makeTransaction('readwrite', objectStoreName, (store) => store.put(data, key));
    }

    public async multiPut<T>(objectStoreName: string, entries: Array<{ key: IDBValidKey; value: T }>): Promise<void> {
        return this.makeTransaction('readwrite', objectStoreName, (store) => {
            for (const entry of entries) {
                store.put(entry.value, entry.key);
            }
        });
    }

    public async get<T>(objectStoreName: string, key: IDBValidKey): Promise<T> {
        return this.makeTransaction<T>('readonly', objectStoreName, (store) => store.get(key));
    }

    public async getAll<T>(objectStoreName: string): Promise<T[]> {
        return this.makeTransaction<T[]>('readonly', objectStoreName, (store) => store.getAll());
    }

    public async removeRecordingDir(objectStoreName: string, key: IDBValidKey): Promise<void> {
        return this.makeTransaction('readwrite', objectStoreName, (store) => store.delete(key));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async makeTransaction<T = any>(
        mode: IDBTransactionMode,
        objectStoreName: string,
        operation: (store: IDBObjectStore) => IDBRequest | undefined | void
    ): Promise<T> {
        const db = await this.initPromise;
        return new Promise<T>((resolve, reject) => {
            const transaction = db.transaction(objectStoreName, mode);

            const store = transaction.objectStore(objectStoreName);
            const idbRequest = operation(store);

            transaction.oncomplete = () => {
                if (idbRequest) {
                    resolve(idbRequest.result as T);
                } else {
                    // @ts-expect-error
                    resolve();
                }
            };
            transaction.onabort = transaction.onerror = () => reject(transaction.error);
        });
    }

    public close() {
        this.db?.close();
    }

    public async removeDatabase() {
        return removeDatabase(this.dbName);
    }

    public async clearStorage() {
        return Promise.all(
            this.objectStoreNames.map((storeName) => {
                return this.makeTransaction('readwrite', storeName, (store) => store.clear());
            })
        );
    }
}
